/* POC isolated carousel rotation v1 — one 4-card Shopify section request per section. */
(function () {
  'use strict';
  if (window.__pocRotationV1) return;
  window.__pocRotationV1 = true;

  const STORAGE_PREFIX = 'poc:rotation:v1:';
  const processed = new WeakSet();
  const fallbackPerSection = new WeakMap();
  const inFlightKeys = new Set();
  const memoryStore = Object.create(null); // session fallback if storage blocked

  function safeGet(key) {
    try { return window.localStorage.getItem(key); }
    catch (_error) { return memoryStore[key] || null; }
  }
  function safeSet(key, value) {
    memoryStore[key] = value;
    try { window.localStorage.setItem(key, value); } catch (_error) {}
  }
  function randomInteger(n) {
    if (n <= 1) return 0;
    if (window.crypto && window.crypto.getRandomValues) {
      const result = new Uint32Array(1);
      window.crypto.getRandomValues(result);
      return result[0] % n;
    }
    return Math.floor(Math.random() * n);
  }
  function shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = randomInteger(i + 1);
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }
  function toInt(value) { return Number.parseInt(value, 10) || 0; }

  function configFor(section) {
    const total = toInt(section.dataset.pocTotal);
    const maxPool = toInt(section.dataset.pocMaxPool);
    const pool = Math.min(total, maxPool > 0 ? maxPool : 25000, 25000);
    const sort = section.dataset.pocSort || 'manual';
    const handle = section.dataset.pocHandle || '';
    const identity = section.dataset.pocCollectionId || handle;
    if (!handle || !identity || !pool) return null;
    return {
      total: pool,
      sourceTotal: total,
      pageCount: Math.ceil(pool / 4),
      sort: sort,
      handle: handle,
      key: STORAGE_PREFIX + identity + ':' + sort + ':' + (maxPool || 'all')
    };
  }

  function readState(cfg) {
    let state = null;
    try { state = JSON.parse(safeGet(cfg.key) || 'null'); } catch (_error) {}
    if (!state || state.total !== cfg.total || state.sourceTotal !== cfg.sourceTotal || !Array.isArray(state.remaining) ||
        state.remaining.some(function (page) {
          return !Number.isInteger(page) || page < 1 || page > cfg.pageCount;
        })) {
      state = {total: cfg.total, sourceTotal: cfg.sourceTotal, remaining: []};
    }
    return state;
  }

  // Claim a 4-item page *before* the request: duplicate carousels (e.g. Men twice
  // on a PDP) cannot select the same group when both are loading simultaneously.
  function reservePage(cfg) {
    const state = readState(cfg);
    if (!state.remaining.length) {
      state.remaining = shuffle(Array.from({length: cfg.pageCount}, (_, i) => i + 1));
    }
    const page = state.remaining.pop();
    safeSet(cfg.key, JSON.stringify(state));
    return page;
  }

  function releasePage(cfg, page) {
    const state = readState(cfg);
    if (state.remaining.indexOf(page) === -1) {
      state.remaining.push(page);
      safeSet(cfg.key, JSON.stringify(state));
    }
  }

  function setupNavigation(section, track) {
    const prev = section.querySelector('.rcc-nav-prev, .slider-nav-prev');
    const next = section.querySelector('.rcc-nav-next, .slider-nav-next');
    if (prev) prev.addEventListener('click', function () {
      track.scrollBy({left: -(track.clientWidth * 0.8), behavior: 'smooth'});
    });
    if (next) next.addEventListener('click', function () {
      track.scrollBy({left: track.clientWidth * 0.8, behavior: 'smooth'});
    });
  }

  function announceNewCards(track) {
    // The existing delegated product-card.js handles variant and Add to Cart.
    // Judge.me may need its standard refresh signal for cards inserted via AJAX.
    try { document.dispatchEvent(new CustomEvent('poc:products:rotated', {detail: {track: track}})); }
    catch (_error) {}
  }

  function restoreFallback(section) {
    section.classList.add('poc-rotation-ready');
    section.classList.add('rcc-shuffled');
  }

  async function rotate(section, cfg) {
    if (!window.fetch || section.dataset.pocRotationDone) return;
    const track = section.querySelector('.rcc-track, .slider-track');
    if (!track) return;

    // Never duplicate a simultaneous query to the same page/collection.
    const page = reservePage(cfg);
    const queryKey = cfg.key + ':' + page;
    if (inFlightKeys.has(queryKey)) return;
    inFlightKeys.add(queryKey);

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const base = root.replace(/\/?$/, '/') + 'collections/' + encodeURIComponent(cfg.handle);
    const url = new URL(base, window.location.origin);
    url.searchParams.set('section_id', 'poc-rotation-feed');
    url.searchParams.set('page', String(page));
    if (cfg.sort !== 'manual') url.searchParams.set('sort_by', cfg.sort);

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeout = controller ? window.setTimeout(function () { controller.abort(); }, 6000) : null;

    try {
      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        signal: controller ? controller.signal : undefined
      });
      if (!response.ok) throw new Error('Rotation feed returned ' + response.status);
      const html = await response.text();
      const pageDoc = new DOMParser().parseFromString(html, 'text/html');
      const feed = pageDoc.querySelector('[data-poc-rotation-feed]');
      if (!feed) throw new Error('Rotation feed markup missing');

      const incoming = Array.from(feed.querySelectorAll(':scope > .poc-rotation-feed-item'));
      if (!incoming.length) throw new Error('Rotation feed empty');
      const indexStart = (page - 1) * 4;
      const allowedCount = Math.max(0, Math.min(4, cfg.total - indexStart));
      const selected = shuffle(incoming.slice(0, allowedCount));
      if (!selected.length) throw new Error('Rotation page outside pool');

      const itemClass = section.classList.contains('rcc-wrapper') ? 'rcc-item' : 'slider-item';
      const fragment = document.createDocumentFragment();
      const selectedIds = new Set();
      selected.forEach(function (item) {
        const id = item.dataset.pocProductId;
        if (id) selectedIds.add(id);
        const outer = document.createElement('div');
        outer.className = itemClass;
        outer.appendChild(item.firstElementChild);
        fragment.appendChild(outer);
      });

      // Last page might contain fewer than four unused products. The original
      // fallback cards fill remaining slots; repeats are allowed ONLY here.
      const fallback = fallbackPerSection.get(section) || [];
      if (cfg.total >= 4 && selected.length < 4) {
        let appendedCount = selected.length;
        for (const node of fallback) {
          const id = node.dataset.pocProductId;
          if (id && selectedIds.has(id)) continue;
          const copy = node.cloneNode(true);
          copy.className = itemClass;
          fragment.appendChild(copy);
          appendedCount += 1;
          if (id) selectedIds.add(id);
          if (appendedCount >= 4) break;
        }
      }

      track.replaceChildren(fragment);
      track.scrollLeft = 0;
      section.dataset.pocRotationDone = 'true';
      restoreFallback(section);
      announceNewCards(track);
    } catch (error) {
      releasePage(cfg, page);
      restoreFallback(section); // Fail open: original Liquid cards stay usable.
      if (window.console && console.warn) console.warn('POC rotation using original cards:', error);
    } finally {
      if (timeout) window.clearTimeout(timeout);
      inFlightKeys.delete(queryKey);
    }
  }

  function initSection(section) {
    if (!section || processed.has(section)) return;
    const track = section.querySelector('.rcc-track, .slider-track');
    if (!track) return;
    processed.add(section);
    // Keep the original HTML available if the network request fails.
    fallbackPerSection.set(section, Array.from(track.children).filter(function (node) {
      return node.matches('.rcc-item, .slider-item');
    }).map(function (node) {
      const copy = node.cloneNode(true);
      copy.dataset.pocProductId = node.dataset.pocProductId || '';
      return copy;
    }));
    setupNavigation(section, track);

    const cfg = configFor(section);
    if (!cfg) { restoreFallback(section); return; }
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        observer.disconnect();
        rotate(section, cfg);
      }, {rootMargin: '350px 0px'});
      observer.observe(section);
    } else {
      rotate(section, cfg);
    }
  }

  function init(root) {
    const context = root || document;
    context.querySelectorAll('.rcc-wrapper[data-poc-rotation], .highlighted-collection[data-poc-rotation]').forEach(initSection);
    if (context.matches && context.matches('.rcc-wrapper[data-poc-rotation], .highlighted-collection[data-poc-rotation]')) initSection(context);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
